/*
 * @Description: 成员实时位置页面
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-03-09 11:04:19
 */
import React, { useCallback, useEffect, useState, useRef } from 'react'
import { Layout } from 'antd'
import './index.scss'
import MapContainer, { MapContainerRef } from './components/MapContainer'
import MemberList from './components/MemberList'
import Filter from './components/Filter'
import LocationSettings, {
  LocationSettingsRef
} from './components/LocationSettings'
import CommonModal, { CommonModalRef } from './components/CommonModal'
import sensors, { sensorsRole } from '../../utils/sensors'
import contact_us_icon from '../../images/contact_us.png'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useVip
} from '../../app/hooks'
import ContactUS from '../components/ContactUS'
import actionTypes from '../../constants/actionTypes'
import brandImg from './images/member-location-vip-required.png'
import alertBgImage from './images/member-location-alert.png'
import {
  fetchRealtimeLocationData,
  fetchLocationConfigData,
  setLocationConfigData
} from '../../actions/memberLocation'
import CONFIG from '../../constants/config'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

interface IProps {
  [key: string]: any
}

// 成员信息类型
export type MemberInfo = {
  groupId: string
  userId: string
  userName: string
  headimgurl: string
  departmentName: string
  active: boolean
  activeInfo: string
  policyGroupRole: number
  lng: number
  lat: number
  address: string // 详细地址
  groupName: string // 团队名称
}

// 实时位置响应数据类型
export type RealtimeLocationResp = {
  status: number
  msg: string
  activeCount: number
  memberInfo: MemberInfo[]
}

// 定位配置响应数据类型
export type LocationConfigResp = {
  code: number
  msg: string
  toast_msg: string
  data: {
    status: number
    msg: string
    isAdmin: boolean
    policyGroupRole: number
    groupConfig: {
      enable: boolean
      startTime: string
      endTime: string
      trackDescription: string
    }
  }
}

const MemberLocation: React.FC<IProps> = () => {
  const dispatch = useAppDispatch()
  const mapRef = useRef<MapContainerRef>(null)
  const commonModalRef = useRef<CommonModalRef>(null)
  const locationSettingsRef = useRef<LocationSettingsRef>(null)
  const group = useAppSelector((state) => state.group)
  const myRole = group.myRole
  const isAdmin =
    myRole === CONFIG.memberRoleTypes.ADMIN ||
    myRole === CONFIG.memberRoleTypes.MASTER_ADMIN

  const userID = useAppSelector((state) => state.main.user?.user_id)

  const { isSVip } = useVip()
  /** 成员位置页准入：超级会员 + 管理员 */
  const canAccessMemberLocation = isSVip && isAdmin

  /**
   * 将VIP类型映射为字符串状态
   * @param group - Redux中的group状态
   * @returns VIP状态字符串: 'nonvip' | 'vip' | 'svip' | 'vvip'
   */
  const getVipStatusString = useCallback((group: any): string => {
    const isVip = group.isVip
    const vipKind = group.vipInfo?.vip_kind

    if (!isVip) {
      return 'nonvip'
    }

    switch (vipKind) {
      case CONFIG.VIP_KIND.VIP: // 1
        return 'vip'
      case CONFIG.VIP_KIND.SVIP: // 2
        return 'svip'
      case CONFIG.VIP_KIND.VVIP: // 3
        return 'vvip'
      default:
        return 'nonvip'
    }
  }, [])

  // 实时位置数据
  const [locationData, setLocationData] = useState<RealtimeLocationResp>({
    status: 0,
    msg: '',
    activeCount: 0,
    memberInfo: []
  })

  // 活跃成员列表弹窗显示状态
  const [showMemberList, setShowMemberList] = useState(true)

  const groupState = useAppSelector((state) => state.group)
  const { groupId } = groupState

  // 筛选状态
  const [filterData, setFilterData] = useState({
    departmentIDList: [],
    activeStatus: 0,
    userIDList: [],
    selectedDepartments: [],
    selectedNormalUserIDs: [],
    selectedDepartmentUserIDs: []
  })

  // 定位配置状态
  const [locationConfig, setLocationConfig] = useState({
    isAdmin: false,
    policyGroupRole: 0,
    groupConfig: {
      enable: false,
      startTime: '00:00',
      endTime: '23:59',
      trackDescription: ''
    }
  })

  // 定位配置提示弹窗状态
  const [showLocationTipModal, setShowLocationTipModal] = useState(false)

  // 点击上报
  const sensorsClick = useCallback(
    (actionType: string, params?: { [key: string]: string }) => {
      sensors.track('workgroup_b4311_c7663_click', {
        actionType: actionType,
        vipLevel: getVipStatusString(group),
        role: sensorsRole(myRole),
        groupID: groupId,
        track_sign: 'aworkgroup.b4311.c7663.click.workgroup_b4311_c7663_click',
        requirement_id: 322,
        ...params
      })
    },
    [groupId, getVipStatusString, group, myRole]
  )

  // 请求实时位置数据
  const requestRealtimeLocation = useCallback(() => {
    dispatch({
      type: actionTypes.SET_LOADING,
      data: true
    })

    const params = {
      groupId,
      activeStatus: filterData.activeStatus ?? 0,
      // 添加筛选参数
      ...(filterData.departmentIDList.length > 0 && {
        departmentIDList: filterData.departmentIDList
      }),
      ...(filterData.userIDList.length > 0 && {
        userIDList: filterData.userIDList
      })
    }

    dispatch(fetchRealtimeLocationData(params))
      .then((res) => {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })

        sensors.track('workgroup_b4311_c7663_exposure', {
          bool1: filterData.activeStatus ?? false,
          list1: params.userIDList?.length
            ? params.userIDList.concat(params.departmentIDList ?? [])
            : params.departmentIDList,
          track_sign:
            'aworkgroup.b4311.c7663.exposure.workgroup_b4311_c7663_exposure',
          requirement_id: 322,
          vipLevel: getVipStatusString(group),
          role: sensorsRole(myRole),
          groupID: groupId,
          ...params
        })

        setLocationData(res)
      })
      .catch(() => {
        dispatch({
          type: actionTypes.SET_LOADING,
          data: false
        })

        setLocationData({
          status: 0,
          msg: '网络异常',
          activeCount: 0,
          memberInfo: []
        })
      })
  }, [dispatch, groupId, filterData])

  // 查询定位配置信息
  const requestLocationConfig = useCallback(() => {
    if (!groupId) return

    const params = {
      userID: userID,
      groupID: groupId
    }

    dispatch(fetchLocationConfigData(params))
      .then((res: any) => {
        console.log('原始接口返回数据:', res)
        //         {
        //     "status": 0,
        //     "msg": "",
        //     "isAdmin": false,
        //     "policyGroupRole": 1,
        //     "groupConfig": {
        //         "enable": false,
        //         "startTime": "00:00",
        //         "endTime": "00:00",
        //         "trackDescription": "团队成员在00:00-00:00期间打开APP会上报实时位置"
        //     }
        // }
        if (res && res.status === 0) {
          const configData = {
            isAdmin: res.isAdmin || false,
            policyGroupRole: res.policyGroupRole || 0,
            groupConfig: {
              enable: res.groupConfig?.enable || false,
              startTime: res.groupConfig?.startTime || '00:00',
              endTime: res.groupConfig?.endTime || '23:59',
              trackDescription: res.groupConfig?.trackDescription || ''
            }
          }

          console.log('解析后的配置数据:', configData)
          setLocationConfig(configData)

          // 检查是否需要显示提示弹窗
          const { enable } = configData.groupConfig
          if (!enable) {
            setShowLocationTipModal(true)
          }
        } else {
          console.log('数据解析失败，res:', res)
        }
      })
      .catch((err) => {
        console.error('查询定位配置失败:', err)
      })
  }, [dispatch, groupId])

  // 保存定位配置
  const saveLocationConfig = useCallback(
    (configData) => {
      if (!dispatch) return Promise.reject('dispatch not available')

      return dispatch(setLocationConfigData(configData))
        .then((res) => {
          if (res && res.status === 0) {
            console.log('保存定位配置成功')
            // 保存成功后重新获取配置
            requestLocationConfig()
            // 保存成功后重新获取实时位置数据
            requestRealtimeLocation()
            return res
          } else {
            console.error('保存定位配置失败:', res)
            return Promise.reject(res)
          }
        })
        .catch((err) => {
          console.error('保存定位配置失败:', err)
          return Promise.reject(err)
        })
    },
    [dispatch, requestLocationConfig]
  )

  // 处理筛选条件变化
  const handleChangeFilter = useCallback((newFilterData) => {
    setFilterData(newFilterData)
  }, [])

  // 组件挂载时请求数据
  useEffect(() => {
    if (groupId) {
      requestRealtimeLocation()
      requestLocationConfig()
    }
  }, [requestRealtimeLocation, requestLocationConfig])

  // 点击地图标记
  const handleClickMarker = useCallback(
    (member: MemberInfo) => {
      sensorsClick('locate', {
        string1: member.userId,
        string2: member.userName,
        string3: member.address,
        string4: member.activeInfo
      })
      // 调用地图的centerToMember方法定位到该成员
      if (mapRef.current) {
        mapRef.current.centerToMember(member)
      }
      console.log('点击成员:', member)
    },
    [sensorsClick]
  )

  // 显示活跃成员列表
  const handleShowMemberList = useCallback(() => {
    sensorsClick('openPanel')
    setShowMemberList(true)
  }, [sensorsClick])

  // 关闭活跃成员列表
  const handleCloseMemberList = useCallback(() => {
    setShowMemberList(false)
    sensorsClick('closePanel')
  }, [sensorsClick])

  // 点击成员列表中的成员，定位到地图上
  const handleMemberListClick = useCallback(
    (member: MemberInfo) => {
      sensorsClick('locate', {
        string1: member.userId,
        string2: member.userName,
        string3: member.address,
        string4: member.activeInfo
      })
      // 调用地图的centerToMember方法定位到该成员
      if (mapRef.current) {
        mapRef.current.centerToMember(member)
      }
      console.log('点击成员列表项:', member)
    },
    [sensorsClick]
  )

  const VipRequired = React.memo(() => {
    const payment = usePayment()

    useEffect(() => {
      sensors.track('workgroup_b4311_c7664_exposure', {
        vipLevel: getVipStatusString(group),
        role: sensorsRole(myRole),
        groupID: groupId,
        track_sign:
          'aworkgroup.b4311.c7664.exposure.workgroup_b4311_c7664_exposure',
        requirement_id: 322
      })
    }, [groupId])

    return (
      <Content className="vip-required">
        <img src={brandImg} alt="" />
        <p>
          秒级掌握成员工作时段实时位置，告别外勤管理盲区，精细化管控团队每一步行动，让任务追踪、效率监管精准到“米”！
          {'\n\n'}
          开通超级会员，即刻终结团队管理失控感，做掌控全局的高效管理者！
        </p>
        <button
          className="pay-btn"
          onClick={() => {
            payment.openSvip('memberLocation', false, true, false)
            sensors.track('workgroup_b4311_c7664_click', {
              actionType: 'goBuy',
              vipLevel: getVipStatusString(group),
              role: sensorsRole(myRole),
              groupID: groupId,
              track_sign:
                'aworkgroup.b4311.c7664.click.workgroup_b4311_c7664_click',
              requirement_id: 322
            })
          }}
        >
          开通超级会员
        </button>
      </Content>
    )
  })

  return (
    <Layout className="member-location-container">
      <PageHeader
        className="Header"
        ghost={false}
        title="人员实时定位"
        subTitle="团队管理效率爆破式提升，管理员必入的控场神器！"
        extra={
          <ContactUS
            fromPage="人员实时定位"
            track={() => {
              sensors.track('workgroup_b4311_c7664_click', {
                actionType: 'contactUs',
                vipLevel: getVipStatusString(group),
                role: sensorsRole(myRole),
                groupID: groupId,
                track_sign:
                  'aworkgroup.b4311.c7664.click.workgroup_b4311_c7664_click',
                requirement_id: 322
              })
            }}
          >
            <span className="contact-item">
              <img style={{ width: '105px' }} src={contact_us_icon} />
            </span>
          </ContactUS>
        }
      />
      {canAccessMemberLocation ? (
        <Content className="content">
          <Filter
            handleChangeFilter={handleChangeFilter}
            filterData={filterData}
            sensorsClick={sensorsClick}
          />
          {!showMemberList && (
            <div className="location-info" onClick={handleShowMemberList}>
              <div className="active-count">
                活跃成员:
                {locationData.memberInfo
                  ? locationData.memberInfo.length
                  : 0}{' '}
                人
              </div>
            </div>
          )}
          {isAdmin && (
            <LocationSettings
              ref={locationSettingsRef}
              locationEnabled={locationConfig.groupConfig.enable}
              startTime={locationConfig.groupConfig.startTime}
              endTime={locationConfig.groupConfig.endTime}
              onLocationToggle={(enabled, startTime, endTime) => {
                console.log(
                  '定位开关状态:',
                  enabled,
                  '时间范围:',
                  startTime,
                  '-',
                  endTime
                )

                // 构造保存请求的数据（管理员模式）
                const saveData = {
                  userID: userID,
                  groupID: groupId,
                  enable: enabled,
                  startTime: enabled ? startTime : '00:00',
                  endTime: enabled ? endTime : '23:59'
                }

                // 调用保存接口
                saveLocationConfig(saveData)
                  .then(() => {
                    console.log('定位开关保存成功')
                    setLocationConfig({
                      ...locationConfig,
                      groupConfig: {
                        ...locationConfig.groupConfig,
                        enable: enabled,
                        startTime: enabled ? startTime : '00:00',
                        endTime: enabled ? endTime : '23:59'
                      }
                    })
                  })
                  .catch((err) => {
                    console.error('定位开关保存失败:', err)
                  })
              }}
              onTimeRangeChange={(startTime, endTime) => {
                console.log('时间范围变化:', startTime, endTime)
                if (!locationConfig.groupConfig.enable) {
                  return
                }

                // 构造保存请求的数据（管理员模式）
                const saveData = {
                  userID: userID,
                  groupID: groupId,
                  enable: locationConfig.groupConfig.enable,
                  startTime,
                  endTime
                }

                // 调用保存接口
                saveLocationConfig(saveData)
                  .then(() => {
                    console.log('时间范围保存成功')
                  })
                  .catch((err) => {
                    console.error('时间范围保存失败:', err)
                  })
              }}
              sensorsClick={sensorsClick}
            />
          )}
          {isAdmin && (
            <MapContainer
              ref={mapRef}
              locationData={locationData}
              handleClickMarker={handleClickMarker}
            />
          )}
          {isAdmin && (
            <MemberList
              open={showMemberList}
              memberList={locationData.memberInfo || []}
              totalCount={
                locationData.memberInfo ? locationData.memberInfo.length : 0
              }
              onClose={handleCloseMemberList}
              onMemberClick={handleMemberListClick}
            />
          )}
        </Content>
      ) : (
        <VipRequired />
      )}

      {/* 定位配置提示弹窗 */}
      {!!canAccessMemberLocation && (
        <CommonModal
          ref={commonModalRef}
          open={showLocationTipModal}
          onCancel={() => setShowLocationTipModal(false)}
          onOk={() => {
            setShowLocationTipModal(false)
            if (locationSettingsRef.current) {
              locationSettingsRef.current.handleErrorModalOk()
            }
          }}
          title="设置【实时定位时间】"
          content="开启定位后，本功能生效"
          buttonText="知道了"
          backgroundImage={alertBgImage}
        />
      )}
    </Layout>
  )
}

export default MemberLocation
